/**
 * 콘텐츠 관리 > 진단 테스트 탭.
 * superadmin이 등록한 AIQ(age)/레벨 테스트(grade) 세트를 read-only로 조회·미리보기.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { ArrowLeft } from 'lucide-react'
import { adminDiagnosticsApi, type QuizSetSummary } from '@/api/adminDiagnostics'
import { getQuizSet, type QuizSetDetail } from '@/api/quizSet'
import { aiqSetName } from '@/utils/aiqSetName'
import { compareLevelSets, isPreschoolLevelSet, levelSetName } from '@/utils/levelSetName'
import { QuizPreviewPanel } from './QuizPreviewPanel'

function displaySetName(set: QuizSetSummary, t: TFunction<'admin'>): string {
  if (!set.levelValue) return set.name
  if (set.levelType === 'age') {
    const name = aiqSetName(set.levelValue)
    return name ? t(`diagnosticSets.${name.key}`, name.params) : set.name
  }
  if (set.levelType === 'grade') {
    const name = levelSetName(set.levelValue, set.semester)
    return name ? t(`diagnosticSets.${name.key}`, name.params) : set.name
  }
  return set.name
}

/** 세트 상세 → 출제 순서대로 quizId 평면화 (백엔드 flatten과 동일 순서). */
export function flattenQuizIds(detail: QuizSetDetail | undefined): string[] {
  if (!detail) return []
  const ids: string[] = []
  const members = [...detail.members].sort((a, b) => a.sequenceOrder - b.sequenceOrder)
  for (const m of members) {
    if (m.kind === 'group' && m.group) {
      for (const it of [...m.group.items].sort((a, b) => a.sequenceOrder - b.sequenceOrder)) {
        ids.push(String(it.quiz.quizId))
      }
    } else if (m.quiz) {
      ids.push(String(m.quiz.quizId))
    }
  }
  return ids
}

export function DiagnosticSetsTab() {
  const { t } = useTranslation('admin')
  const setsQ = useQuery({
    queryKey: ['quiz-sets-all'],
    queryFn: () => adminDiagnosticsApi.listQuizSets(),
  })
  const [inspect, setInspect] = useState<QuizSetSummary | null>(null)

  if (inspect) {
    return <SetInspector set={inspect} onClose={() => setInspect(null)} />
  }

  const sets = setsQ.data?.data.data ?? []
  const ageSets = sets.filter((s) => s.levelType === 'age')
  const gradeSets = sets.filter((s) => s.levelType === 'grade' && !isPreschoolLevelSet(s)).sort(compareLevelSets)

  return (
    <div className="space-y-8">
      <p className="text-sm text-gray-500">{t('diagnosticSets.readOnlyNotice')}</p>
      {setsQ.isError ? (
        <div className="p-8 text-center text-sm text-rose-600 bg-white rounded-lg shadow">
          {t('diagnosticSets.loadFailed')}
        </div>
      ) : (
        <>
          <SetGroup title={t('diagnosticSets.aiqGroup')} sets={ageSets} loading={setsQ.isLoading} onInspect={setInspect} />
          <SetGroup title={t('diagnosticSets.levelGroup')} sets={gradeSets} loading={setsQ.isLoading} onInspect={setInspect} printable />
        </>
      )}
    </div>
  )
}

/** 원생 없이 레벨 테스트 세트를 출력하는 화면(이름 칸 빈칸). */
function diagnosticSetPrintHref(setId: number): string {
  return `/admin/contents/diagnostic-sets/${setId}/print`
}

function SetGroup({ title, sets, loading, onInspect, printable = false }: {
  title: string
  sets: QuizSetSummary[]
  loading: boolean
  onInspect: (s: QuizSetSummary) => void
  /** 레벨 테스트 세트만 시험지 출력을 제공한다. */
  printable?: boolean
}) {
  const { t } = useTranslation('admin')
  return (
    <section>
      <h2 className="text-lg font-semibold mb-3">{title}</h2>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">…</div>
        ) : sets.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('diagnosticSets.empty')}</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3">{t('diagnosticSets.name')}</th>
                <th className="px-4 py-3">{t('diagnosticSets.level')}</th>
                <th className="px-4 py-3 text-right">{t('diagnosticSets.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {sets.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {displaySetName(s, t)}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{s.levelValue ?? '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center justify-end gap-3">
                      <button type="button" onClick={() => onInspect(s)} className="text-primary-600 hover:underline">
                        {t('diagnosticSets.preview')}
                      </button>
                      {/* PDF 다운로드는 출력 화면에 있으므로 목록에는 출력하기만 둔다. */}
                      {printable && (
                        <button
                          type="button"
                          onClick={() => window.open(diagnosticSetPrintHref(s.id), '_blank', 'noopener,noreferrer')}
                          className="text-primary-600 hover:underline"
                        >
                          {t('diagnosticSets.print')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  )
}

function SetInspector({ set, onClose }: { set: QuizSetSummary; onClose: () => void }) {
  const { t } = useTranslation('admin')
  const detailQ = useQuery({
    queryKey: ['quiz-set-detail', set.id],
    queryFn: () => getQuizSet(set.id),
  })
  const quizIds = useMemo(() => flattenQuizIds(detailQ.data), [detailQ.data])
  const [current, setCurrent] = useState(0)
  const total = quizIds.length

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('diagnosticSets.backToList')}
        </button>
        <h2 className="text-base font-semibold text-gray-800">
          {displaySetName(set, t)}
        </h2>
        <span className="ml-auto text-sm text-gray-500">{total > 0 ? `${current + 1} / ${total}` : '0 / 0'}</span>
      </div>

      {detailQ.isLoading ? (
        <div className="p-8 text-center text-gray-500">…</div>
      ) : detailQ.isError ? (
        <div className="py-12 text-sm text-center rounded-lg border border-dashed border-rose-200 bg-rose-50 text-rose-600">
          {t('diagnosticSets.loadFailed')}
        </div>
      ) : total === 0 ? (
        <div className="py-12 text-sm text-center rounded-lg border border-dashed border-gray-300 bg-gray-50 text-gray-400">
          {t('diagnosticSets.empty')}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {quizIds.map((id, i) => (
              <button
                key={`${id}-${i}`}
                type="button"
                onClick={() => setCurrent(i)}
                className={`w-7 h-7 inline-flex items-center justify-center text-xs font-medium rounded-full border transition-colors ${
                  i === current
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <QuizPreviewPanel quizId={quizIds[current]} />
          </div>
        </>
      )}
    </div>
  )
}
